/**
 * champsEdition.ts, la TRANSFORMATION d'un fichier de champs.
 *
 * POURQUOI CE FICHIER EXISTE : cette fonction vivait dans le corps d'un
 * `useCallback` de `StudioScreen`, ou elle etait intestable. Or c'est le seul
 * endroit du studio qui REECRIT un fichier du depot : une erreur ici ne casse
 * pas un affichage, elle casse l'app de quelqu'un. Sortie du composant, elle est
 * PURE (des entrees, une sortie, aucun etat React), donc elle se teste avec
 * `node --test`, et `packages/shell/tests/champs.test.mjs` la couvre cas par
 * cas, y compris les pieges que la relecture ne voit pas.
 *
 * CE QU'ELLE NE FAIT PAS : elle ne valide pas. La validation est faite par le
 * pont, qui ecrit puis rejoue le garde-fou de la CI et DEFAIT si le fichier est
 * refuse. Recopier le schema ici en ferait une seconde source de verite, et
 * c'est elle qui mentirait en premier.
 */
import { COULEURS_DU_DESIGN, estCouleur } from '@taki/data-model';
import type { Couleur } from '@taki/data-model';
import type { DeclarationLue } from './modele.js';
import { clefDeChamp } from './identifiants.mjs';

/** Un champ tel que le fichier le porte. */
type Champ = DeclarationLue['jeux'][number]['champs'][number];

/** Ce qu'un brouillon change a la cible d'un lien ; voir `EditionChamps.cibles`. */
export interface CibleEditee {
  family?: string;
  type?: string;
  titre?: string;
}

/**
 * LA FAMILLE D'UN LIEN QUI N'EN A PAS ENCORE : un champ qui DEVIENT un lien
 * recoit une cible tout de suite, sans quoi le schema refuserait l'ecriture
 * entiere. `contacts` parce que c'est, de loin, la famille la plus visee (un
 * client, un fournisseur, un responsable) ; la ligne « cible » apparait a la
 * meme ecriture et se change en un geste.
 */
export const FAMILLE_DE_LIEN_PAR_DEFAUT = 'contacts';

/**
 * L'ANNUAIRE DES PERSONNES DE L'ORGANISATION, seule cible d'un champ
 * `personne` (`FAMILLE_PERSONNES` de `@taki/data-model`, recopie : ce module ne
 * tire pas le modele de donnees pour une chaine, le schema fait foi).
 */
export const FAMILLE_PERSONNES = 'personnes';

/** Ce qu'un brouillon du studio demande de changer. */
export type EditionChamps = {
  /** Le fichier tel qu'il a ete lu. */
  declaration: DeclarationLue;
  /** clef -> nouveau libelle. */
  libelles: Map<string, string>;
  /** clef -> nouveau type. */
  types: Map<string, string>;
  /** clef -> catalogue tape, valeurs separees par des virgules. */
  valeurs: Map<string, string>;
  /** Modifications structurées, ciblées par valeur stockée. */
  options?: Map<string, PatchOptionsChamp>;
  /** clef -> `'oui'` ou `'non'`. */
  requis: Map<string, string>;
  /**
   * clef -> l'exemple rendu dans une case VIDE (le `placeholder` du fichier).
   *
   * Une chaine VIDE RETIRE la clef, elle ne la pose pas vide : le schema exige
   * au moins un caractere, et « pas d'exemple » veut dire « le defaut du socle
   * par nature » (« Ecrire ici », « 0 », « Choisir »), jamais un placeholder
   * blanc.
   */
  exemples: Map<string, string>;
  /**
   * clef de JEU (`famille::type`) -> la famille choisie.
   *
   * C'est le seul geste qui touche un jeu et non un champ, et le seul qui
   * traverse LES DEUX fichiers de declaration : le jeu d'une porte
   * (`taki.screens.json`) pointe le jeu declare ici. Il ne s'offre que sur un
   * TROU NOMME (`@a-decider/<porte>`) : sur une famille reelle, `sdk.<famille>`
   * est ecrit dans le code de l'app, et la changer ici ne changerait pas ce
   * code.
   */
  familles: Map<string, string>;
  /**
   * clef -> CE QUE VISE un champ `lien` : la famille, le type local, le champ
   * qui nomme la fiche. Chaque clef n'est posee que si la ligne a ete touchee ;
   * une chaine VIDE sur `type` ou `titre` RETIRE la clef (« toute la famille »,
   * « la convention du socle »). Ignore sur un champ qui n'est pas un lien.
   */
  cibles?: Map<string, CibleEditee>;
  /** Les libelles des champs a AJOUTER. Leur clef se derive ici. */
  neufs: string[];
  /** Le rang du jeu qui recoit les champs neufs. */
  jeuVise: number;
};

/**
 * Le fichier de champs APRES le brouillon. L'entree n'est jamais modifiee.
 */
export function declarationSuivante(edition: EditionChamps): DeclarationLue {
  const { declaration, libelles, types, valeurs, requis, exemples, neufs, jeuVise, familles } =
    edition;
  const cibles = edition.cibles ?? new Map<string, CibleEditee>();

  /*
   * LES CLEFS DEJA PRISES, TOUS JEUX CONFONDUS. Deriver deux fois la meme clef
   * d'un libelle proche ("Etat" et "État") ferait deux champs qui ecrivent au
   * meme endroit : le second gagnerait en silence.
   */
  const prises = new Set(declaration.jeux.flatMap((jeu) => jeu.champs.map((c) => c.key)));

  const aAjouter: Champ[] = [];
  for (const label of neufs) {
    const base = clefDeChamp(label);
    if (base === '') continue;
    let key = base;
    let n = 2;
    while (prises.has(key)) key = `${base}${n++}`;
    prises.add(key);
    /* `text` par defaut : c'est le type le moins engageant, celui qui accepte ce
       qu'on y met. Le changer se fera champ par champ. */
    aAjouter.push({ key, label, type: 'text' } as Champ);
  }

  return {
    ...declaration,
    jeux: declaration.jeux.map((jeu, rang) => ({
      ...jeu,
      /* LA FAMILLE SE REMPLACE, ELLE NE S'AJOUTE PAS : le trou nomme DISPARAIT
         du fichier, il ne cohabite pas avec la famille choisie. Deux facons de
         dire d'ou vient un jeu, et c'est la mauvaise qu'on lirait un jour. */
      ...(familles.has(`${jeu.family}::${jeu.type ?? ''}`)
        ? { family: familles.get(`${jeu.family}::${jeu.type ?? ''}`)! }
        : {}),
      champs: [
        ...jeu.champs.map((champ) =>
          champSuivant(champ, { libelles, types, valeurs, requis, exemples, cibles, options: edition.options }),
        ),
        /*
         * LES CHAMPS NEUFS VONT AU JEU DE LA PORTE REGARDEE, pas au premier.
         * Vrai pour une app a un seul jeu, faux des la premiere app qui en a
         * deux : un champ ajoute depuis la porte B atterrissait dans le jeu A,
         * sans un mot.
         */
        ...(rang === jeuVise ? aAjouter : []),
      ],
    })),
  };
}

/** Un champ apres le brouillon. */
function champSuivant(
  champ: Champ,
  m: Pick<EditionChamps, 'libelles' | 'types' | 'valeurs' | 'requis' | 'exemples'> & {
    cibles: Map<string, CibleEditee>;
    options?: Map<string, PatchOptionsChamp>;
  },
): Champ {
  let suite = champ;

  if (m.libelles.has(champ.key)) {
    suite = { ...suite, label: m.libelles.get(champ.key)! };
  }

  if (m.types.has(champ.key)) {
    const type = m.types.get(champ.key)!;
    suite = { ...suite, type } as Champ;
    /*
     * UN TYPE QUI CESSE D'ETRE UN CHOIX PERD SON CATALOGUE, et l'inverse en
     * exige un. Le schema refuse les deux autres cas, donc les laisser passer
     * ferait rejeter l'ecriture entiere pour une incoherence que l'ecran
     * pouvait resoudre seul.
     */
    const aBesoin = type === 'select' || type === 'multiselect';
    if (!aBesoin && suite.options !== undefined) {
      const { options: _retire, ...sansOptions } = suite;
      suite = sansOptions as Champ;
    }
    if (aBesoin && (suite.options ?? []).length === 0) {
      suite = { ...suite, options: [{ value: 'a-definir', label: 'A definir' }] };
    }
    /*
     * MEME REGLE POUR LA CIBLE D'UN LIEN : un champ qui cesse d'etre un lien
     * perd sa cible (le schema la refuse sur tout autre type), un champ qui le
     * devient en recoit une, la famille par defaut, qu'on change ensuite.
     */
    /*
     * UNE PERSONNE a une cible facultative et FIXE (l'annuaire des personnes) :
     * celle d'un lien ne lui convient pas, et la sienne ne convient pas a un
     * lien. Le passage de l'un a l'autre repart donc de zero.
     */
    const cibleAdmise = type === 'lien'
      ? suite.reference?.family !== FAMILLE_PERSONNES
      : type === 'personne' && suite.reference?.family === FAMILLE_PERSONNES;
    if (!cibleAdmise && suite.reference !== undefined) {
      const { reference: _retire, ...sansCible } = suite;
      suite = sansCible as Champ;
    }
    if (type === 'lien' && suite.reference === undefined) {
      suite = { ...suite, reference: { family: FAMILLE_DE_LIEN_PAR_DEFAUT } };
    }
  }

  const cible = m.cibles.get(champ.key);
  if (cible !== undefined && suite.type === 'lien') {
    /*
     * LA CIBLE SE MODIFIE CLEF PAR CLEF : la famille se REMPLACE (jamais vide,
     * un lien vise toujours une famille), le type et le titre se posent ou se
     * RETIRENT (une chaine vide veut dire « toute la famille », « la convention
     * du socle »), jamais poses vides : le schema exige un caractere.
     */
    const courante = suite.reference ?? { family: FAMILLE_DE_LIEN_PAR_DEFAUT };
    const family = cible.family?.trim();
    const reference: NonNullable<Champ['reference']> = {
      ...courante,
      ...(family === undefined || family === '' ? {} : { family }),
    };
    for (const clef of ['type', 'titre'] as const) {
      const valeur = cible[clef];
      if (valeur === undefined) continue;
      if (valeur.trim() === '') delete reference[clef];
      else reference[clef] = valeur.trim();
    }
    suite = { ...suite, reference };
  }
  if (cible !== undefined && suite.type === 'personne' && cible.titre !== undefined) {
    /*
     * LE SEUL REGLAGE D'UNE PERSONNE : ce qui la nomme. Vide RETIRE la
     * reference entiere (la convention du socle), jamais une reference sans
     * titre, qui dirait deux fois la meme chose.
     */
    const titre = cible.titre.trim();
    if (titre === '') {
      if (suite.reference !== undefined) {
        const { reference: _retire, ...sansCible } = suite;
        suite = sansCible as Champ;
      }
    } else {
      suite = { ...suite, reference: { family: FAMILLE_PERSONNES, titre } };
    }
  }

  if (m.options?.has(champ.key)) {
    if (suite.type !== 'select' && suite.type !== 'multiselect') throw new Error(`Le champ « ${champ.key} » n’est pas un champ à choix.`);
    suite = { ...suite, options: patchOptionsChamp((suite.options ?? []) as OptionDeChamp[], m.options.get(champ.key)!) };
  } else if (m.valeurs.has(champ.key)) {
    /* Le texte historique décrit une liste complète. Un renommage sûr passe
       par options.rename, qui conserve explicitement la valeur stockée.
       Aucun appariement par position : un déplacement ne change pas les clés. */
    const parLibelle = new Map((champ.options ?? []).map((o) => [o.label.trim(), o]));
    const listees = m.valeurs
      .get(champ.key)!
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '');
    const clefsPrises = new Set<string>();
    const suivantes = listees.map((label) => {
      const connue = parLibelle.get(label);
      if (connue !== undefined) {
        clefsPrises.add(connue.value);
        return connue;
      }
      const base = clefDeChamp(label) || 'valeur';
      let value = base;
      let n = 2;
      while (clefsPrises.has(value)) value = `${base}${n++}`;
      clefsPrises.add(value);
      return { value, label };
    });
    /* Un catalogue vide ferait refuser l'ecriture entiere par le schema : on
       garde alors celui d'avant, et la ligne le redira a la relecture. */
    const anciennes = (champ.options ?? []).filter(option => !clefsPrises.has(option.value)).map(option => ({ ...option, archived: true }));
    suite = { ...suite, options: [...suivantes, ...anciennes] };
  }

  if (m.exemples.has(champ.key)) {
    /* MEME REGLE QUE `required` : « pas d'exemple » s'ecrit en RETIRANT la
       clef, jamais en posant une chaine vide que le schema refuserait. */
    const exemple = m.exemples.get(champ.key)!.trim();
    if (exemple === '') {
      if (suite.placeholder !== undefined) {
        const { placeholder: _retire, ...sansExemple } = suite;
        suite = sansExemple as Champ;
      }
    } else {
      suite = { ...suite, placeholder: exemple };
    }
  }

  if (m.requis.has(champ.key)) {
    /* `required` est FACULTATIF dans le schema, et « non » s'ecrit en RETIRANT
       la clef, pas en posant `false` : deux facons de dire non produiraient deux
       fichiers pour un meme sens. */
    if (m.requis.get(champ.key) === 'oui') {
      suite = { ...suite, required: true };
    } else if (suite.required !== undefined) {
      const { required: _retire, ...sansRequis } = suite;
      suite = sansRequis as Champ;
    }
  }

  return suite;
}

/** Modification explicite d'options : leur identité est la valeur stockée. */
export type OptionDeChamp = { archived?: boolean; value: string; label: string; tone?: Couleur; parent?: string };
export type PatchOptionsChamp = {
  add?: { label: string; tone?: OptionDeChamp['tone']; parent?: string }[];
  /** Le parent de chaque option d'un champ dependant (`dependDe`) ; `null` : pour tous. */
  parents?: { value: string; parent: string | null }[];
  rename?: { value: string; label: string }[];
  remove?: string[];
  archive?: string[];
  reactivate?: string[];
  replace?: { from: string; to: string }[];
  clear?: string[];
  order?: string[];
  colors?: { value: string; tone: NonNullable<OptionDeChamp['tone']> }[];
};
/** Les clefs de la grille du design, offertes a Lia (`tones`) ; un hex `#rgb` / `#rrggbb` est accepte aussi. */
export const TONS_OPTIONS: readonly Couleur[] = COULEURS_DU_DESIGN.map((couleur) => couleur.clef);

export function patchOptionsChamp(before: readonly OptionDeChamp[], patch: PatchOptionsChamp): OptionDeChamp[] {
  let after = before.map((option) => ({ ...option }));
  const reserved = new Set(before.map((option) => option.value));
  const find = (value: string) => {
    const option = after.find((candidate) => candidate.value === value);
    if (option === undefined) throw new Error(`Option inconnue « ${value} ». Valeurs valides : ${after.map((o) => o.value).join(', ')}.`);
    return option;
  };
  const labelOf = (label: string) => {
    if (typeof label !== 'string' || label.trim() === '') throw new Error('Le libellé d’une option doit être non vide.');
    return label.trim();
  };
  const toneOf = (tone: string) => {
    if (!estCouleur(tone)) throw new Error(`Couleur inconnue « ${tone} ». Couleurs valides : ${TONS_OPTIONS.join(', ')}, ou un hex #rrggbb.`);
    return tone;
  };
  const duplicateValues = (values: string[], operation: string) => {
    if (new Set(values).size !== values.length) throw new Error(`Une valeur est répétée dans ${operation}.`);
  };
  duplicateValues((patch.rename ?? []).map((o) => o.value), 'rename');
  duplicateValues(patch.remove ?? [], 'remove');
  duplicateValues(patch.reactivate ?? [], 'reactivate');
  duplicateValues((patch.colors ?? []).map((o) => o.value), 'colors');
  for (const rename of patch.rename ?? []) find(rename.value).label = labelOf(rename.label);
  for (const color of patch.colors ?? []) find(color.value).tone = toneOf(color.tone);
  duplicateValues((patch.parents ?? []).map((o) => o.value), 'parents');
  for (const { value, parent } of patch.parents ?? []) {
    const option = find(value);
    if (parent === null || parent === '') delete option.parent;
    else option.parent = parent;
  }
  const retirees = [...(patch.remove ?? []), ...(patch.archive ?? []), ...(patch.clear ?? []), ...(patch.replace ?? []).map(x => x.from)];
  duplicateValues(retirees, 'retrait');
  for (const value of retirees) find(value).archived = true;
  for (const value of patch.reactivate ?? []) {
    if (retirees.includes(value)) throw new Error('Une option ne peut pas être retirée et réactivée ensemble.');
    delete find(value).archived;
  }
  for (const addition of patch.add ?? []) {
    const label = labelOf(addition.label);
    const base = clefDeChamp(label) || 'valeur';
    let value = base;
    let n = 2;
    while (reserved.has(value)) value = `${base}${n++}`;
    reserved.add(value);
    after.push({ value, label, ...(addition.tone === undefined ? {} : { tone: toneOf(addition.tone) }),
      ...(addition.parent === undefined || addition.parent === '' ? {} : { parent: addition.parent }) });
  }
  for (const remplacement of patch.replace ?? []) {
    find(remplacement.from);
    if (remplacement.from === remplacement.to || find(remplacement.to).archived === true || retirees.includes(remplacement.to)) throw new Error('Choisir une destination active, distincte et conservée.');
  }
  if (after.length === 0) throw new Error('Un champ à choix doit garder au moins une option.');
  if (new Set(after.map((o) => o.label)).size !== after.length) throw new Error('Deux options ne peuvent pas porter le même libellé.');
  if (patch.order !== undefined) {
    duplicateValues(patch.order, 'order');
    if (patch.order.length !== after.length || patch.order.some((value) => !after.some((o) => o.value === value))) {
      throw new Error(`order doit contenir exactement toutes les valeurs après modification : ${after.map((o) => o.value).join(', ')}.`);
    }
    after = patch.order.map((value) => find(value));
  }
  return after;
}
